import { ArrowUpCircle, Building2, ChevronDown, ChevronRight, MapPin, MoreHorizontal, Plus, ScreenShare, Settings, Sparkles, Trash2 } from 'lucide-react';
import { DropdownMenu } from 'radix-ui';
import { useEffect, useId, useMemo, useReducer, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { AddSiteDialog, useManagesEndUsers, useOwnsEveryCustomer } from '../customers/AddCustomer';
import { DeleteCustomerDialog } from '../customers/DeleteCustomer';
import { CustomerMigrationCard } from '../customers/Migration';
import { CustomerFalconCard } from '../customers/Falcon';
import { CustomerUsers } from '../customers/CustomerUsers';
import { recordPath } from '../records/paths';
import { ConsentSetting, summarizeConsent, useRemoteConsent } from '../remote/ConsentSetting';
import { AutomaticUpdatesSetting, useAutomaticUpdates } from '../settings/AutomaticUpdatesSetting';
import { Workspace } from '../records/Workspace';
import { useScope } from '../scope/scope';
import { needsLookQuery, useCatalog, useFacets, useRecord, useSearch } from '../search/hooks';
import { Button, buttonClass } from '../ui/Button';
import { ErrorPanel } from '../ui/errors';
import { Card, CopyButton, Skeleton } from '../ui/misc';
import { STATUS_ORDER, StatusBar, statusMeta } from '../ui/status';
import { cn } from '../ui/cn';
import { PolicyDraftPanel } from '../policies/PolicyDraftPanel';
import { PageHeader } from './PageHeader';

// Keyed by id: requests keep their last answer while the next loads, and one customer's or site's
// numbers must never show as another's. A refresh of the same one still keeps its data on screen.
export function CustomerPage() {
  const { id } = useParams();
  return <Customer key={id} id={id} />;
}

function Customer({ id }: { id: string | undefined }) {
  const { mspId } = useScope();
  const record = useRecord('organization', id);
  const { catalog, isSuccess: catalogLoaded, isError: catalogFailed, error: catalogError, refetch: retryCatalog } = useCatalog();
  const facets = useFacets(id ? { version: 1, kinds: ['device'], filters: [{ mode: 'and', clauses: [{ field: 'organizationId', op: 'in', values: [id] }] }] } : null);
  // Same definition as Today: a reported problem, no status, or no check-in for a week.
  const base = needsLookQuery();
  const needsQuery = id ? { ...base, limit: 1, filters: [...base.filters!, { mode: 'and' as const, clauses: [{ field: 'organizationId' as const, op: 'in' as const, values: [id] }] }] } : null;
  const needs = useSearch(needsQuery);
  // Those needing a look that still report Healthy: their check-in is over a week old (offline workstations aren't counted, WRMM-255).
  const needFacets = useFacets(needsQuery);
  const sites = useMemo(() => catalog.sites.filter(s => s.organizationId === id), [catalog.sites, id]);
  const siteCounts = new Map(facets.data?.sites.map(s => [s.id, s.count]));
  const owner = useOwnsEveryCustomer();
  const managesEndUsers = useManagesEndUsers(id);
  const [addingSite, setAddingSite] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // The header action opens the settings and brings them into view once they have rendered.
  const [jumped, jump] = useReducer((n: number) => n + 1, 0);
  const settingsToggle = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!jumped) return;
    settingsToggle.current?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    settingsToggle.current?.focus({ preventScroll: true });
  }, [jumped]);
  if (record.isError) return <Card><ErrorPanel error={record.error} onRetry={() => void record.refetch()} /></Card>;
  const name = record.data?.title;
  const status = facets.data?.status;
  const needing = needs.data?.total ?? 0;
  const unverified = needFacets.data?.status.healthy ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader truncateTitle eyebrow={<Link to={`/w/${mspId}/customers`} className="hover:text-ink">Customers</Link>}
        title={name ?? <Skeleton className="h-8 w-56" />}
        // Sites come from the catalog: no count until it loads, rather than “0 sites”.
        description={facets.data ? `${catalogLoaded ? `${sites.length} site${sites.length === 1 ? '' : 's'} · ` : ''}${facets.data.total} device${facets.data.total === 1 ? '' : 's'}` : undefined}
        actions={<>
          <Link to={`/w/${mspId}/ask?${new URLSearchParams({ q: `What needs a look at ${name ?? 'this customer'}?`, org: id ?? '', kind: 'device' })}`} className={buttonClass('secondary')}>
            <Sparkles className="size-4 text-brand" />Brief me
          </Link>
          {id && <Button onClick={() => { setSettingsOpen(true); jump(); }}><Settings className="size-4 text-ink-3" />Customer settings</Button>}
          <CopyButton value={typeof window !== 'undefined' ? window.location.href : ''} label="Copy link" className="size-9 border border-line bg-panel" />
          {/* WRMM-336: owners covering every customer delete an empty one, with their key. */}
          {owner && id && (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger className={buttonClass('ghost', 'icon')} aria-label="More actions"><MoreHorizontal className="size-4" /></DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content align="end" sideOffset={6} className="z-50 w-56 rounded-xl border border-line bg-raised p-1 shadow-pop animate-pop">
                  <DropdownMenu.Item onSelect={() => setDeleting(true)} className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-[13.5px] text-offline outline-none data-[highlighted]:bg-hover">
                    <Trash2 className="size-4" />Delete customer…
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          )}
        </>} />
      {owner && id && deleting && <DeleteCustomerDialog organizationId={id} name={name} open onOpenChange={setDeleting} />}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
        <Card className="p-5">
          <div className="flex items-center gap-2 text-[13px] font-medium text-ink-2"><Building2 className="size-4 text-ink-3" />Device health</div>
          {facets.isError || needs.isError ? (
            <ErrorPanel className="mt-2" error={facets.error ?? needs.error} onRetry={() => { void facets.refetch(); void needs.refetch(); }} />
          ) : status && needs.data ? (<>
            <p className="mt-2 text-[15px]">{needing ? <><span className="font-semibold">{needing}</span> of {facets.data!.total} need a look{unverified ? `, including ${unverified} reporting Healthy without a check-in this week` : ''}.</>
              : !facets.data!.total ? 'No devices yet.' : status.offline ? `Nothing needs a look. ${status.offline} ${status.offline === 1 ? 'is' : 'are'} offline.`
              : 'Every device reports healthy and checked in this week.'}</p>
            <StatusBar className="mt-3 h-2.5" counts={status} quietOffline />
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
              {STATUS_ORDER.map(s => { const m = statusMeta[s], Icon = m.icon; return (
                <span key={s} className="inline-flex items-center gap-1.5 text-[13px] text-ink-2"><Icon className={cn('size-3.5', s === 'offline' ? 'text-ink-3' : m.text)} /><span className="tabular font-semibold text-ink">{status[s]}</span>{s === 'unknown' ? 'no status' : m.label.toLowerCase()}</span>
              ); })}
            </div>
          </>) : <div className="mt-3 space-y-3"><Skeleton className="w-1/2" /><Skeleton className="h-2.5 w-full" /></div>}
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-2 text-[13px] font-medium text-ink-2"><MapPin className="size-4 text-ink-3" /><span className="flex-1">Sites</span>
            {owner && id && <Button size="sm" variant="ghost" onClick={() => setAddingSite(true)}><Plus className="size-3.5" />Add site</Button>}</div>
          {owner && id && <AddSiteDialog organizationId={id} customer={name} open={addingSite} onOpenChange={setAddingSite} />}
          {catalogFailed ? <ErrorPanel className="mt-2" error={catalogError} onRetry={() => void retryCatalog()} />
            : !catalogLoaded ? <div className="mt-2 grid gap-1.5 sm:grid-cols-2"><Skeleton className="h-10" /><Skeleton className="h-10" /></div> : (
            <ul className="mt-2 grid gap-1.5 sm:grid-cols-2">
              {sites.map(s => (
                <li key={s.id}>
                  <Link to={recordPath(mspId, 'site', s.id)} className="group flex items-center gap-2 rounded-lg border border-line px-3 py-2 transition-colors hover:border-line-strong hover:bg-hover">
                    <span className="min-w-0 flex-1 truncate font-medium">{s.title}</span>
                    {facets.data && <span className="tabular text-[12.5px] text-ink-3">{siteCounts.get(s.id) ?? 0} devices</span>}
                    <ChevronRight className="size-4 text-ink-3" />
                  </Link>
                </li>
              ))}
              {!sites.length && <li className="text-ink-3">No sites you can access.</li>}
            </ul>
          )}
        </Card>
      </div>
      {id && <Workspace mode="device" base={{ org: [id] }} locked={['org']} placeholder={`Filter ${name ?? 'this customer'}’s devices — or “offline”, “not seen this week”…`}
        heading={<h2 className="text-[15px] font-semibold">Devices</h2>} />}
      {id && <PolicyDraftPanel target={{ kind: 'customer', organizationId: id }} />}
      {id && managesEndUsers && <CustomerUsers organizationId={id} name={name} />}
      {id && <CustomerSettings organizationId={id} name={name} open={settingsOpen} onOpenChange={setSettingsOpen} toggleRef={settingsToggle} />}
      {/* WRMM-229: the customer's CrowdStrike box and Falcon child CID, for workspace owners. */}
      {id && owner && <CustomerFalconCard organizationId={id} />}
      {/* WRMM-185: workspace owners move a customer off NinjaOne and SentinelOne. */}
      {id && owner && <CustomerMigrationCard organizationId={id} name={name} />}
    </div>
  );
}

/**
 * Per-customer settings that rarely change (WRMM-132): collapsed at the bottom so the devices come first, with their
 * current values in one line. Each setting loads on its own; a failure says so here and blocks nothing above.
 */
function CustomerSettings({ organizationId, name, open, onOpenChange, toggleRef }: {
  organizationId: string; name?: string; open: boolean; onOpenChange: (open: boolean) => void; toggleRef: React.RefObject<HTMLButtonElement | null>;
}) {
  const panel = useId();
  const consent = useRemoteConsent(organizationId);
  const updates = useAutomaticUpdates(organizationId);
  const value = <T,>(q: { isPending: boolean; isError: boolean; data?: T }, text: (data: T) => string) =>
    q.isPending ? 'loading…' : q.isError ? 'couldn’t load' : text(q.data!);
  return (
    <Card>
      <h2>
        <button ref={toggleRef} type="button" aria-expanded={open} aria-controls={panel} onClick={() => onOpenChange(!open)}
          className={cn('flex w-full scroll-mt-20 items-center gap-2 px-5 py-4 text-left hover:bg-hover', open ? 'rounded-t-xl' : 'rounded-xl')}>
          <ChevronDown className={cn('size-4 shrink-0 text-ink-3 transition-transform', !open && '-rotate-90')} />
          <span className="min-w-0">
            <span className="block text-[15px] font-semibold">Customer settings</span>
            {!open && <span className="block text-[12.5px] font-normal text-ink-3">
              Remote sessions: {value(consent, summarizeConsent)} · Agent updates: {value(updates, u => (u.enabled ? 'automatic' : 'paused'))}
            </span>}
          </span>
        </button>
      </h2>
      {/* Kept mounted while hidden: a save in progress survives collapsing, and opening doesn't refetch. */}
      <div id={panel} hidden={!open} className="grid gap-6 border-t border-line p-5 lg:grid-cols-2">
        <div>
          <h3 className="flex items-center gap-2 text-[13px] font-medium text-ink-2"><ScreenShare className="size-4 text-ink-3" />Remote sessions</h3>
          <p className="mb-3 mt-1 text-[12.5px] text-ink-3">What happens when a technician connects to {name ?? 'this customer'}’s computers. Sessions already asked for keep the setting they started with; changes are in the audit trail.</p>
          <ConsentSetting organizationId={organizationId} name={name} />
        </div>
        <div>
          <h3 className="flex items-center gap-2 text-[13px] font-medium text-ink-2"><ArrowUpCircle className="size-4 text-ink-3" />Agent updates</h3>
          <p className="mb-3 mt-1 text-[12.5px] text-ink-3">Whether {name ?? 'this customer'}’s computers install new WITTCO agent versions by themselves. Changes are in the audit trail.</p>
          <AutomaticUpdatesSetting organizationId={organizationId} />
        </div>
      </div>
    </Card>
  );
}

export function SitePage() {
  const { id } = useParams();
  return <Site key={id} id={id} />;
}

function Site({ id }: { id: string | undefined }) {
  const { mspId } = useScope();
  const record = useRecord('site', id);
  if (record.isError) return <Card><ErrorPanel error={record.error} onRetry={() => void record.refetch()} /></Card>;
  const site = record.data;
  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={<span className="flex items-center gap-1.5"><Link to={`/w/${mspId}/customers`} className="hover:text-ink">Customers</Link><ChevronRight className="size-3.5" />
          {site ? <Link to={recordPath(mspId, 'organization', site.organizationId)} className="hover:text-ink">{site.organizationName}</Link> : '…'}</span>}
        title={site?.title ?? <Skeleton className="h-8 w-48" />} description={site ? `Site at ${site.organizationName}` : undefined}
        actions={site && (
          <Link to={`/w/${mspId}/ask?${new URLSearchParams({ q: `What needs a look at ${site.organizationName} · ${site.title}?`, site: site.id, kind: 'device' })}`} className={buttonClass('secondary')}>
            <Sparkles className="size-4 text-brand" />Brief me
          </Link>
        )} />
      {site && <PolicyDraftPanel target={{ kind: 'site', organizationId: site.organizationId!, siteId: site.id }} />}
      {id && <Workspace mode="device" base={{ site: [id] }} locked={['org', 'site']} heading={<h2 className="text-[15px] font-semibold">Devices at this site</h2>} />}
    </div>
  );
}
